import { useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

import {
	$createWorkspaceCheckout,
	$workspaceCheckoutState,
} from "../../internal/workspaces/workspace-checkout.atom";
import { type Workspace } from "../../internal/workspaces/workspace.types";

/**
 * Hook for initiating workspace billing checkout flows.
 *
 * Exposes the reactive checkout state (idle/pending/success/error), derived helpers
 * (`error`, `isLoading`, etc.), and a memoized `startCheckout` helper that kicks off
 * the checkout atom for a specific workspace. Consumers can branch on the typed billing
 * errors via the `error` field or the underlying `checkoutState`.
 */
export const useWorkspaceCheckout = (workspaceId: string | undefined) => {
	const checkoutState = useAtomValue($workspaceCheckoutState(workspaceId));
	const createCheckoutAtom = useSetAtom($createWorkspaceCheckout);

	const startCheckout = async (workspace: Workspace) => createCheckoutAtom(workspace);

	const error = checkoutState.status === "error" ? checkoutState.error : undefined;

	return {
		checkoutState,
		startCheckout,
		error,
		isError: checkoutState.status === "error",
	};
};
